import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/apiBase';

const API_URL = getApiBaseUrl();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const getDeviceId = () => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'device_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  };

  // Create axios instance that updates with token changes
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
    });
    
    // Add request interceptor to always use latest token
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      config.headers['X-Device-ID'] = getDeviceId();
      return config;
    });
    
    return instance;
  }, []);

  const fetchUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      setToken(storedToken);
      if (data?.language_preference) {
        try {
          localStorage.setItem('app_language', data.language_preference);
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // identifier: { email } or { phone, phone_country_code }
  const login = async (identifier, password) => {
    const { data } = await api.post('/auth/login', { ...identifier, password });
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (email, password, name, phone, phoneCountryCode, refCode = null, acceptedTerms = false) => {
    const url = refCode ? `/auth/signup?ref=${refCode}` : '/auth/signup';
    const { data } = await api.post(url, {
      email, password, name, phone, phone_country_code: phoneCountryCode,
      accepted_terms: acceptedTerms,
    });
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {}
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const permissions = user?.permissions || [];
  const role = user?.role || 'user';
  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role !== 'user';
  const hasPermission = (permission) => isSuperAdmin || permissions.includes(permission);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, signup, logout, refreshUser, api,
      isAdmin, isSuperAdmin, permissions, hasPermission, isPremium: user?.is_premium,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
